import { useCallback } from "react";
import { useLocalStorage } from "./useLocalStorage";
import type { AttentionCard } from "../types/dashboard";

const KEY = "sacristy.attention-preferences.v1";

// Card kinds Colleen can toggle on/off in Settings. outreach_pending
// is intentionally absent — that signal moved to the Ask Sacristy AI
// chat and is hard-filtered before reaching this hook.
export type ToggleableKind = Exclude<AttentionCard["kind"], "outreach_pending">;

export interface ToggleableKindMeta {
  kind: ToggleableKind;
  label: string;
  description: string;
}

// Order here is the order they render in the Settings panel.
export const TOGGLEABLE_KINDS: ToggleableKindMeta[] = [
  {
    kind: "stale_sub_request",
    label: "Stale sub-requests",
    description:
      "Sub-requests still open after a few days without a claimer.",
  },
  {
    kind: "past_48h_gap",
    label: "Last-minute gaps",
    description:
      "Masses within 48 hours that still have an empty slot.",
  },
  {
    kind: "quarter_prep",
    label: "Quarterly outreach reminder",
    description:
      "Reminder when the next quarter's availability email is coming up.",
  },
  {
    kind: "annual_response",
    label: "Annual response requests",
    description: "Periodic volunteer review prompts.",
  },
  {
    kind: "action_card",
    label: "Pending action cards",
    description:
      "Chat-prepared plans you haven't applied or dismissed yet.",
  },
];

type Preferences = Record<ToggleableKind, boolean>;

const DEFAULTS: Preferences = {
  stale_sub_request: true,
  past_48h_gap: true,
  quarter_prep: true,
  annual_response: true,
  action_card: true,
};

// Reads/writes the per-browser preferences. New kinds added to
// TOGGLEABLE_KINDS later default to `true` — saved preferences only
// override known kinds, so a missing entry isn't treated as "hidden."
export function useAttentionPreferences() {
  const [stored, setStored] = useLocalStorage<Partial<Preferences>>(KEY, {});

  const prefs: Preferences = { ...DEFAULTS, ...stored };

  const set = useCallback(
    (kind: ToggleableKind, visible: boolean) => {
      setStored((prev) => ({ ...prev, [kind]: visible }));
    },
    [setStored],
  );

  const isVisible = (kind: AttentionCard["kind"]): boolean => {
    if (kind === "outreach_pending") return false;
    return prefs[kind] ?? true;
  };

  return { prefs, set, isVisible };
}
